import { mapLocationsDtoToLocations } from 'modules/locations/domain/data-mappers/location-data-mapper';
import { locationsToOptions } from 'modules/locations/domain/data-mappers/locations-to-options';
import { LocationOptionResponseDto, LocationResponseDto } from 'modules/locations/domain/interfaces/response-dto';
import { Location, LocationOption } from 'modules/locations/domain/interfaces/location';
import { baseApi } from 'shared/api/base-api';

/** Тег перечня локаций: по нему мутации сбрасывают именно список, а не карточки. */
export const LOCATIONS_LIST_TAG = { type: 'Location' as const, id: 'PARTIAL-LIST' };

/**
 * Список локаций для страницы `/locations`. Запрос повторяет
 * `locationsRepository.fetchLocations`, но живёт в RTK Query: пагинация страницы
 * построена на `usePagedList`, которому нужны `data`/`currentData` и инвалидация
 * по тегам, а не накопительный слайс.
 *
 * Выборка урезана до того, что рисует строка: карту (`mapImage`) и описание
 * читает карточка локации, списку они не нужны.
 */
const LIST_LOCATIONS_QUERY = `#graphql
  query getTournamentLocations($tournamentId: Int!, $limit: Int!, $offset: Int!, $nameFilter: String) {
    tournament(id: $tournamentId) {
      id
      locations(limit: $limit, offset: $offset, nameFilter: $nameFilter) {
        id
        name
        address {
          id
          addressLine1
          addressLine2
          city
          region
          country
          coords {
            lat
            lon
          }
        }
        locationImages {
          image {
            id
            filename
            externalUrl
          }
          ordinalNumber
        }
        tournament {
          id
        }
      }
    }
  }
`;

export interface ListLocationsArgs {
  tournamentId: number;
  limit: number;
  offset: number;
  nameFilter?: string;
}

interface ListLocationsResponseDto {
  tournament?: {
    id: number;
    locations?: LocationResponseDto[];
  };
}

/** Экспортируется для юнит-тестов: пустой ответ должен давать пустой список, а не падать. */
export function mapListLocationsResponse(raw: ListLocationsResponseDto): Location[] {
  return mapLocationsDtoToLocations(raw?.tournament?.locations ?? []);
}

/** Выпадающий список площадок: только id и имя, первые 50 — так было и у прежнего запроса в репозитории. */
const LIST_LOCATION_OPTIONS_QUERY = `#graphql
  query fetchLocationsOptions($tournamentId: Int!, $limit: Int, $offset: Int) {
    tournament(id: $tournamentId) {
      locations(limit: $limit, offset: $offset) {
        id
        name
      }
    }
  }
`;

const LOCATION_OPTIONS_LIMIT = 50;

interface ListLocationOptionsResponseDto {
  tournament?: { locations?: LocationOptionResponseDto[] };
}

export function mapListLocationOptionsResponse(raw: ListLocationOptionsResponseDto): LocationOption[] {
  return locationsToOptions(raw?.tournament?.locations ?? []);
}

export const locationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Аргумент — id организации (backend `tournamentId`). Тег списка общий с `listLocations`: создание и удаление
     * локации (`invalidateLocationsList`) обновляют и выпадающий список.
     */
    listLocationOptions: build.query<LocationOption[], number>({
      query: (organizerId) => ({
        query: LIST_LOCATION_OPTIONS_QUERY,
        variables: { tournamentId: organizerId, limit: LOCATION_OPTIONS_LIMIT, offset: 0 },
      }),
      transformResponse: mapListLocationOptionsResponse,
      providesTags: [LOCATIONS_LIST_TAG],
    }),
    listLocations: build.query<Location[], ListLocationsArgs>({
      query: ({ tournamentId, limit, offset, nameFilter }) => ({
        query: LIST_LOCATIONS_QUERY,
        variables: { tournamentId, limit, offset, nameFilter },
      }),
      transformResponse: mapListLocationsResponse,
      // Идентификаторы локаций рядом с тегом списка: правка локации обновляет её
      // строку в перечне, а создание и удаление бьют по тегу списка.
      providesTags: (result) => [
        LOCATIONS_LIST_TAG,
        ...(result ?? []).map((location) => ({ type: 'Location' as const, id: location.id })),
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useListLocationsQuery, useListLocationOptionsQuery } = locationsApi;
